<script setup lang="ts">
import {
  quoteTotals,
  taxRatesLabel,
  type CatalogProduct,
  type QuoteClientCheck,
  type QuoteClientMatch,
  type QuoteDraft,
  type QuoteDraftClient,
  type QuoteDraftLine,
} from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { api } from '../../api';
import { errorText } from '../../composables/useResource';
import { useMembers } from '../../composables/useMembers';
import { usePlan } from '../../composables/usePlan';
import { eur, eurCents } from '../../format';
import { changedFields, editOf, notesLineCount, type QuoteEdit } from '../../quoteDraft';
import { usePlanStore } from '../../stores/plan';
import { useQuoteDraftStore } from '../../stores/quoteDraft';
import { useToastStore } from '../../stores/toast';
import QuoteClientBlock from './QuoteClientBlock.vue';
import QuoteLines from './QuoteLines.vue';
import '../../styles/quote.css';

/**
 * Lot AT : le brouillon de devis (maquette devis-r2-1710, onglet A), grand volet ouvert depuis le bloc « Après le
 * R1 » du volet du R1 ou une carte « Préparer le devis » / « Envoyer le devis ». 1. Coller les notes, « Proposer un
 * devis » (Claude). 2. Le devis proposé, modifiable. 3. Le client dans Axonaut. Enregistré dans le Cockpit au fil de
 * l'eau ; « Créer le devis dans Axonaut » ouvre l'écran de validation (plan `quote.create`).
 */
const store = useQuoteDraftStore();
const planStore = usePlanStore();
const toast = useToastStore();
const { openPlan } = usePlan();
const { members, load: loadMembers } = useMembers();

const draft = ref<QuoteDraft | null>(null);
const edit = ref<QuoteEdit | null>(null);
const catalog = ref<CatalogProduct[]>([]);
const check = ref<QuoteClientCheck | null>(null);
const loadError = ref('');
const actionError = ref('');
const proposing = ref(false);
const searching = ref(false);
const creating = ref(false);
const notesOpen = ref(false);
const manual = ref(false);
const root = ref<HTMLElement | null>(null);

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
const saveState = ref<SaveState>('idle');
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<void> = Promise.resolve();
/** État enregistré côté serveur (base des différences à envoyer). */
let saved: QuoteEdit | null = null;

const readonly = computed(() => draft.value?.status !== 'DRAFT');
const proposed = computed(
  () => !!draft.value?.proposedAt || (edit.value?.lines.length ?? 0) > 0 || manual.value,
);
const totals = computed(() => quoteTotals(edit.value?.lines ?? []));
const memberName = (id: string | null) => members.value?.find((m) => m.id === id)?.name ?? null;
const r1Line = computed(() => {
  const d = draft.value;
  if (!d) return '';
  const when = d.r1
    ? new Date(d.r1.startAt).toLocaleDateString('fr-FR', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'Europe/Paris',
      })
    : null;
  const by = memberName(d.createdBy);
  return [when ? `Après le R1 du ${when}` : 'Sans R1 dans l’agenda', by ? `préparé par ${by}` : null]
    .filter(Boolean)
    .join(' · ');
});
const notesInfo = computed(() => {
  const d = draft.value;
  if (!d || !edit.value) return '';
  const who = memberName(d.notesBy);
  const at = d.notesAt
    ? new Date(d.notesAt).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Paris',
      })
    : null;
  return [
    `${notesLineCount(edit.value.notes)} lignes`,
    who ? `par ${who}` : null,
    at ? `à ${at.replace(':', ' h ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
});
const fmt = (cents: number) => (cents % 100 === 0 ? eur(cents) : eurCents(cents));

function adopt(next: QuoteDraft): void {
  draft.value = next;
  edit.value = editOf(next);
  saved = editOf(next);
}

async function load(): Promise<void> {
  const target = store.target;
  if (!target) return;
  loadError.value = '';
  actionError.value = '';
  draft.value = null;
  edit.value = null;
  check.value = null;
  manual.value = false;
  notesOpen.value = false;
  try {
    const [opened, products] = await Promise.all([
      api.openQuoteDraft(target),
      catalog.value.length ? Promise.resolve({ items: catalog.value }) : api.quoteCatalog(),
    ]);
    catalog.value = products.items;
    adopt(opened);
    store.markChanged();
  } catch (err) {
    loadError.value = errorText(err);
  }
}

// ------------------------------------------------------------------ Enregistrement au fil de l'eau

function scheduleSave(): void {
  if (readonly.value) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => void flush(), 600);
}

/** Envoie les changements en attente (un envoi à la fois). */
function flush(): Promise<void> {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = null;
  saving = saving.then(async () => {
    const d = draft.value;
    const current = edit.value;
    if (!d || !current || !saved || d.status !== 'DRAFT') return;
    const body = changedFields(saved, current);
    if (Object.keys(body).length === 0) return;
    saveState.value = 'saving';
    const sent = JSON.parse(JSON.stringify(current)) as QuoteEdit;
    try {
      const next = await api.updateQuoteDraft(d.id, body);
      draft.value = next;
      saved = sent;
      saveState.value = 'saved';
      store.markChanged();
    } catch (err) {
      saveState.value = 'error';
      actionError.value = `Enregistrement impossible : ${errorText(err)}`;
    }
  });
  return saving;
}

function setLines(lines: QuoteDraftLine[]): void {
  if (!edit.value) return;
  edit.value = { ...edit.value, lines };
  scheduleSave();
}
function setClient(client: QuoteDraftClient): void {
  if (!edit.value) return;
  edit.value = { ...edit.value, client };
  scheduleSave();
}
function onNotes(value: string): void {
  if (!edit.value) return;
  edit.value = { ...edit.value, notes: value };
  scheduleSave();
}
function onComment(value: string): void {
  if (!edit.value) return;
  edit.value = { ...edit.value, comment: value };
  scheduleSave();
}

// ------------------------------------------------------------------ Gestes

async function propose(): Promise<void> {
  const d = draft.value;
  if (!d || !edit.value) return;
  if (edit.value.notes.trim() === '') {
    actionError.value = 'Colle d’abord tes notes ou la transcription du rendez-vous.';
    return;
  }
  if (
    d.lines.length > 0 &&
    !window.confirm('Remplacer les lignes actuelles par une nouvelle proposition de Claude ?')
  )
    return;
  proposing.value = true;
  actionError.value = '';
  try {
    await flush();
    const next = await api.proposeQuote(d.id);
    adopt(next);
    notesOpen.value = false;
    const doubts = next.lines.filter((l) => l.doubt).length;
    toast.show(
      `Devis proposé : ${next.lines.length} ligne${next.lines.length > 1 ? 's' : ''}${
        doubts ? `, ${doubts} à vérifier` : ''
      }.`,
    );
    store.markChanged();
    if (next.client.mode === null) await searchClient();
  } catch (err) {
    actionError.value = `Proposition impossible : ${errorText(err)}`;
  } finally {
    proposing.value = false;
  }
}

async function searchClient(): Promise<void> {
  const d = draft.value;
  if (!d) return;
  searching.value = true;
  actionError.value = '';
  try {
    await flush();
    const result = await api.searchQuoteClient(d.id);
    adopt(result.draft);
    check.value = result.check;
  } catch (err) {
    actionError.value = `Recherche du client impossible : ${errorText(err)}`;
  } finally {
    searching.value = false;
  }
}

function takeMatch(match: QuoteClientMatch): void {
  if (!edit.value) return;
  const client = edit.value.client;
  setClient({
    ...client,
    mode: 'existing',
    foundBy: 'chosen',
    axonautCompanyId: match.axonautCompanyId,
    clientId: match.clientId,
    company: {
      ...client.company,
      name: match.name,
      siret: match.siret,
      city: match.city ?? client.company.city,
    },
    sources: { ...client.sources, 'company.name': 'axonaut' },
  });
  toast.show('Client existant choisi : rien à créer dans Axonaut.');
}
function dismissMatch(match: QuoteClientMatch): void {
  if (!edit.value) return;
  const client = edit.value.client;
  setClient({ ...client, dismissedMatches: [...client.dismissedMatches, match.axonautCompanyId] });
  toast.show('Un nouveau client sera créé à la validation.');
}
function notThisOne(): void {
  if (!edit.value) return;
  const client = edit.value.client;
  setClient({
    ...client,
    mode: 'new',
    foundBy: null,
    dismissedMatches: client.axonautCompanyId
      ? [...client.dismissedMatches, client.axonautCompanyId]
      : client.dismissedMatches,
    axonautCompanyId: null,
    clientId: null,
  });
}

async function createInAxonaut(): Promise<void> {
  const d = draft.value;
  if (!d) return;
  creating.value = true;
  actionError.value = '';
  try {
    await flush();
    // Client pas encore cherché : la recherche passe d'abord (le plan a besoin de savoir s'il faut le créer).
    if (edit.value?.client.mode === null) await searchClient();
    const run = await openPlan('quote.create', {
      draftId: d.id,
      ...(store.target?.actionId ? { actionId: store.target.actionId } : {}),
    });
    if (run) {
      store.markChanged();
      adopt(await api.quoteDraft(d.id));
    }
  } catch (err) {
    actionError.value = errorText(err);
  } finally {
    creating.value = false;
  }
}

async function setDismissed(dismissed: boolean): Promise<void> {
  const d = draft.value;
  if (!d) return;
  try {
    await flush();
    adopt(dismissed ? await api.dismissQuoteDraft(d.id) : await api.reopenQuoteDraft(d.id));
    store.markChanged();
    toast.show(
      dismissed ? 'Brouillon écarté : les cartes « Préparer le devis » se ferment.' : 'Brouillon repris.',
    );
  } catch (err) {
    actionError.value = errorText(err);
  }
}

async function close(): Promise<void> {
  await flush();
  store.close();
}

function onKey(event: KeyboardEvent): void {
  // L'écran de validation, ouvert par-dessus, se ferme d'abord.
  if (event.key === 'Escape' && store.isOpen && !planStore.isOpen) void close();
}

watch(
  () => store.target,
  (target) => {
    if (!target) return;
    void load();
    void nextTick(() => root.value?.focus());
  },
  { immediate: true },
);
onMounted(() => {
  document.addEventListener('keydown', onKey);
  void loadMembers();
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey);
  if (saveTimer) clearTimeout(saveTimer);
});
</script>

<template>
  <template v-if="store.isOpen">
    <div class="q-backdrop" aria-hidden="true" @click="close"></div>
    <aside
      ref="root"
      class="q-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quote-title"
      tabindex="-1"
      data-testid="quote-drawer"
    >
      <div class="frame-h">
        <div>
          <span class="crumb">Devis · brouillon dans le Cockpit</span>
          <h2 id="quote-title">Devis de {{ draft?.prospect ?? '…' }}</h2>
          <p v-if="draft">{{ r1Line }}</p>
        </div>
        <div class="q-head-ops">
          <span
            v-if="saveState !== 'idle' && !readonly"
            class="saved"
            :class="saveState"
            data-testid="quote-saved"
            ><i aria-hidden="true"></i
            >{{
              saveState === 'saving'
                ? 'Enregistrement…'
                : saveState === 'error'
                  ? 'Pas enregistré'
                  : 'Brouillon enregistré'
            }}</span
          >
          <button class="btn sm ghost" type="button" aria-label="Fermer le brouillon" @click="close">
            ✕
          </button>
        </div>
      </div>

      <div class="frame-b">
        <p v-if="loadError" class="form-error" role="alert">{{ loadError }}</p>
        <p v-else-if="!draft || !edit" class="muted">Ouverture du brouillon…</p>
        <template v-else>
          <div
            v-if="draft.status === 'CREATED' && draft.axonaut"
            class="plan-result"
            data-testid="quote-created"
          >
            <span
              ><b>Devis {{ draft.axonaut.number ?? '' }} créé dans Axonaut</b> (en attente),
              {{ fmt(totals.netCents) }} HT. Rien n’a été envoyé au client : tu le finis à la main dans
              Axonaut, puis tu l’envoies de là-bas.</span
            >
            <div>
              <a
                v-if="draft.axonaut.url"
                class="btn sm primary"
                :href="draft.axonaut.url"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-open-axonaut"
                >Ouvrir le devis dans Axonaut</a
              >
            </div>
          </div>
          <div v-else-if="draft.status === 'DISMISSED'" class="cli-banner new" data-testid="quote-dismissed">
            <span><b>Brouillon écarté.</b> Les cartes « Préparer le devis » de ce R1 sont fermées.</span>
            <button class="btn sm" type="button" data-testid="quote-reopen" @click="setDismissed(false)">
              Reprendre
            </button>
          </div>

          <!-- Étape 1 -->
          <div class="blk" data-testid="quote-step-notes">
            <div class="step-h">
              <span v-if="proposed" class="step-n ok">✓</span><span v-else class="step-n">1</span>
              <h3>Coller le texte du rendez-vous</h3>
            </div>
            <div v-if="!proposed || notesOpen" class="q-notes">
              <label class="field"
                >Tes notes ou la transcription (texte seulement)
                <textarea
                  class="notes-in"
                  :value="edit.notes"
                  :disabled="readonly"
                  data-testid="quote-notes"
                  @input="onNotes(($event.target as HTMLTextAreaElement).value)"
                ></textarea>
              </label>
              <div class="row">
                <button
                  class="btn primary"
                  type="button"
                  :disabled="proposing || readonly"
                  data-testid="quote-propose"
                  @click="propose"
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14" aria-hidden="true">
                    <path d="M8 0l1.6 4.8L14.4 6.4 9.6 8 8 12.8 6.4 8 1.6 6.4 6.4 4.8z" />
                  </svg>
                  <span>{{
                    proposing
                      ? 'Claude lit tes notes…'
                      : proposed
                        ? 'Proposer à nouveau'
                        : 'Proposer un devis'
                  }}</span>
                </button>
                <span class="muted q-small"
                  >Claude lit aussi la fiche R1, les infos du R1, le catalogue Axonaut ({{
                    catalog.filter((p) => !p.disabled).length
                  }}
                  produits) et le panier médian.</span
                >
                <button
                  v-if="!proposed && !readonly"
                  class="linkish"
                  type="button"
                  data-testid="quote-manual"
                  @click="manual = true"
                >
                  Remplir les lignes à la main
                </button>
              </div>
            </div>
            <div v-else class="notes-done" data-testid="quote-notes-done">
              <span><b>Notes collées</b> · {{ notesInfo }}</span>
              <span class="spacer"></span>
              <button
                class="linkish"
                type="button"
                data-testid="quote-notes-toggle"
                @click="notesOpen = true"
              >
                Revoir ou modifier les notes
              </button>
            </div>
          </div>

          <!-- Étape 2 -->
          <div class="blk" data-testid="quote-step-lines">
            <div class="step-h">
              <span class="step-n" :class="{ off: !proposed }">2</span>
              <h3>Le devis proposé</h3>
              <span class="spacer"></span>
              <span v-if="proposed && !readonly" class="muted q-small"
                >modifiable : lignes, prix, remises, commentaire</span
              >
            </div>
            <div v-if="!proposed" class="placeholder">
              Le devis proposé par Claude apparaîtra ici : tu pourras tout modifier avant de créer quoi que ce
              soit dans Axonaut.
            </div>
            <template v-else>
              <div v-if="draft.summary" class="ai" data-testid="quote-summary">
                <div class="ai-h">
                  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M8 0l1.6 4.8L14.4 6.4 9.6 8 8 12.8 6.4 8 1.6 6.4 6.4 4.8z" /></svg
                  >Proposé par Claude d’après tes notes et la fiche R1
                </div>
                <p>{{ draft.summary }}</p>
              </div>
              <QuoteLines
                :lines="edit.lines"
                :catalog="catalog"
                :readonly="readonly"
                @update="setLines"
                @toast="(text) => toast.show(text)"
              />
              <div
                v-if="edit.doubts.length > 0 || draft.missing.length > 0"
                class="doubts"
                data-testid="quote-doubts"
              >
                <h3>Doutes</h3>
                <ul>
                  <li v-for="(d, i) in edit.doubts" :key="`d${i}`">{{ d }}</li>
                </ul>
                <template v-if="draft.missing.length > 0">
                  <h3>À demander au prospect</h3>
                  <ul>
                    <li v-for="(m, i) in draft.missing" :key="`m${i}`">{{ m }}</li>
                  </ul>
                </template>
              </div>
              <div class="totals-wrap">
                <label class="field"
                  >Commentaire du devis (repris dans Axonaut)
                  <textarea
                    rows="4"
                    :value="edit.comment"
                    :disabled="readonly"
                    data-testid="quote-comment"
                    @input="onComment(($event.target as HTMLTextAreaElement).value)"
                  ></textarea>
                </label>
                <div class="totals" aria-live="polite" data-testid="quote-totals">
                  <div>
                    <span>Total HT</span><span data-testid="quote-total-ht">{{ fmt(totals.netCents) }}</span>
                  </div>
                  <div>
                    <span>Remises</span
                    ><span>{{ totals.discountCents ? `− ${fmt(totals.discountCents)}` : fmt(0) }}</span>
                  </div>
                  <div>
                    <span>{{ taxRatesLabel(totals.taxRates) }}</span
                    ><span>{{ fmt(totals.vatCents) }}</span>
                  </div>
                  <div class="ttc">
                    <span>Total TTC</span
                    ><span data-testid="quote-total-ttc">{{ fmt(totals.ttcCents) }}</span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- Étape 3 -->
          <div class="blk" data-testid="quote-step-client">
            <div class="step-h">
              <span class="step-n" :class="{ off: !proposed }">3</span>
              <h3>Le client dans Axonaut</h3>
            </div>
            <QuoteClientBlock
              :client="edit.client"
              :check="check"
              :searching="searching"
              :readonly="readonly"
              @update="setClient"
              @search="searchClient"
              @take="takeMatch"
              @dismiss="dismissMatch"
              @not-this-one="notThisOne"
            />
          </div>
          <p v-if="actionError" class="form-error" role="alert" data-testid="quote-error">
            {{ actionError }}
          </p>
        </template>
      </div>

      <div class="frame-f">
        <span v-if="draft && edit && proposed" class="muted q-small"
          ><b class="q-foot-total" data-testid="quote-foot-total"
            >{{ fmt(totals.netCents) }} HT · {{ fmt(totals.ttcCents) }} TTC</b
          ><template v-if="draft.status === 'DRAFT'">
            · rien n’est créé dans Axonaut avant ta validation</template
          ></span
        >
        <span class="spacer"></span>
        <button
          v-if="draft?.status === 'DRAFT'"
          class="btn ghost"
          type="button"
          data-testid="quote-dismiss"
          @click="setDismissed(true)"
        >
          Écarter le brouillon
        </button>
        <button class="btn" type="button" data-testid="quote-close" @click="close">Fermer</button>
        <button
          v-if="draft?.status === 'DRAFT'"
          class="btn primary"
          type="button"
          :disabled="creating || !edit || edit.lines.length === 0"
          data-testid="quote-create"
          @click="createInAxonaut"
        >
          Créer le devis dans Axonaut
        </button>
      </div>
    </aside>
  </template>
</template>
